/**
 * Numeric foreign-key codec for async-select fields — normalizes a numeric-string
 * id (the driver emits the option `value` as a string) back to a number on save,
 * so the persisted FK matches the numeric data contract. Opt-in per field via
 * `numericId: true` (the factory auto-injects this only for those fields): a
 * blanket numeric coercion on every async-select would turn legitimate string
 * (slug) ids into `NaN`.
 *
 * @module
 */

/**
 * Creates a FieldCodec for `numericId: true` async-select fields.
 *
 * Empty / cleared selection (`'' | null | undefined`): the serialized value is
 * decided by PROPERTY PRESENCE of `numericIdEmptyValue`, never its truthiness —
 * a valid empty sentinel is `0`, which is falsy. When the option is ABSENT the
 * codec returns `undefined` (the editor omits the key); when PRESENT the
 * configured value is sent (e.g. `0` for a real "Unassigned" FK row).
 *
 * Non-empty values: a purely numeric string is coerced to a number; anything
 * else (a slug id) is passed through unchanged. `parse` is identity — the driver
 * resolves the display label from the id via `resolveLabel`.
 *
 * @param {{ numericIdEmptyValue?: number }} [config]
 * @returns {import('../../contracts/forms.js').FieldCodec}
 */
export function createNumericIdCodec(config = {}) {
  const hasEmptyValue = Object.prototype.hasOwnProperty.call(config, 'numericIdEmptyValue');
  const emptyValue = config.numericIdEmptyValue;

  return {
    /** @param {unknown} wireValue */
    parse(wireValue) {
      return wireValue;
    },

    /** @param {unknown} uiValue */
    serialize(uiValue) {
      if (uiValue === '' || uiValue == null) {
        return hasEmptyValue ? emptyValue : undefined;
      }
      const str = String(uiValue);
      return /^\d+$/.test(str) ? Number(str) : uiValue;
    },
  };
}
